import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import TermsOfServiceModal from '../components/TermsOfServiceModal';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import { EyeIcon } from '../components/Icons';
import USIcon from '../assets/us.png';
import PHIcon from '../assets/philippines.png';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';

type LangKey = 'en' | 'fil' | 'bis';

const LANG_OPTIONS: { key: LangKey; label: string; icon: string }[] = [
  { key: 'en', label: 'English', icon: USIcon },
  { key: 'fil', label: 'Filipino', icon: PHIcon },
  { key: 'bis', label: 'Bisaya', icon: PHIcon }
];

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  agreeToTerms?: string;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [langOpen, setLangOpen] = useState<boolean>(false);
  const [selectedLang, setSelectedLang] = useState<LangKey>('en');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState<boolean>(false);
  const [pleaseSignInMessage, setPleaseSignInMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });

  const langRef = React.useRef<HTMLDivElement | null>(null);

  // Close language menu when clicking outside
  React.useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Auto-redirect to login when we show "please sign in" (500 or loginRequired)
  React.useEffect(() => {
    if (!pleaseSignInMessage) return;
    const timer = setTimeout(() => {
      navigate('/login');
    }, 5000);
    return () => clearTimeout(timer);
  }, [pleaseSignInMessage, navigate]);

  const selected = React.useMemo(() => LANG_OPTIONS.find(l => l.key === selectedLang) || LANG_OPTIONS[0], [selectedLang]);

  // Handle language change
  const handleLanguageChange = React.useCallback((langKey: LangKey) => {
    setSelectedLang(langKey);
    setLangOpen(false);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!/(?=.*[@$!%*?&])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one special character (@$!%*?&)';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms agreement validation
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          profile: {
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim()
          }
        }),
      });

      const data = await response.json();

      console.log('Registration response:', response.status, data);

      if (response.ok && data.success) {
        if (data.data?.loginRequired) {
          // Account created but tokens not issued; ask user to sign in
          toast.success('Account created. Please sign in.', {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          setPleaseSignInMessage('Account created. Please sign in.');
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            agreeToTerms: false
          });
          setErrors({});
        } else {
          // Use AuthContext to handle login after successful registration
          login(data.data.token, data.data.refreshToken, data.data.user);

        // Show success toast
        toast.success('Account created successfully! Welcome to SELF PSENsOR.', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

          setShowSuccessModal(true);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            agreeToTerms: false
          });
          setErrors({});
        }
      } else {
        if (response.status === 500) {
          toast.warning('Account may have been created. Please try signing in.', {
            position: "top-center",
            autoClose: 6000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          setPleaseSignInMessage('Account may have been created. Please try signing in.');
        } else {
          toast.error(data.message || 'Registration failed. Please try again.', {
            position: "top-center",
            autoClose: 6000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      }
    } catch (error) {

      toast.error('Registration failed. Please check your connection and try again.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="register-page">
      <div className="register-page__left">
        <div className="register-page__background">
        </div>
      </div>
      
      <div className="register-page__right">
        <div className="lang-dropdown" ref={langRef}>
          <button
            className="lang-dropdown__button"
            onClick={() => setLangOpen(o => !o)}
            aria-haspopup="listbox"
            aria-expanded={langOpen}
          >
            <img src={selected.icon} alt="" />
            <span>{selected.label}</span>
          </button>
          {langOpen && (
            <ul className="lang-dropdown__menu" role="listbox">
              {LANG_OPTIONS.map(opt => (
                <li key={opt.key}>
                  <button
                    className="lang-dropdown__item"
                    role="option"
                    aria-selected={selectedLang === opt.key}
                    onClick={() => handleLanguageChange(opt.key)}
                  >
                    <img src={opt.icon} alt="" />
                    <span>{opt.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="register-page__content">
          {pleaseSignInMessage && (
            <div className="register-page__please-sign-in" role="alert">
              <p className="register-page__please-sign-in-message">{pleaseSignInMessage}</p>
              <Link to="/login" className="register-page__please-sign-in-link">
                Sign in
              </Link>
              <p className="register-page__please-sign-in-redirect">Redirecting to sign in in 5 seconds...</p>
            </div>
          )}
          <div className="register-page__header">
            <h1>Create Account</h1>
            <p>Join LIFE PSENsOR and start your wellness journey today</p>
          </div>

          <form onSubmit={handleSubmit} className="register-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={errors.firstName ? 'error' : ''}
                  placeholder="Enter your first name"
                />
                {errors.firstName && <span className="error-message">{errors.firstName}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={errors.lastName ? 'error' : ''}
                  placeholder="Enter your last name"
                />
                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email address"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={errors.password ? 'error' : ''}
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon show={showPassword} />
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <div className="password-input">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={errors.confirmPassword ? 'error' : ''}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon show={showConfirmPassword} />
                </button>
              </div>
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  className={errors.agreeToTerms ? 'error' : ''}
                />
                <span className="checkmark"></span>
                I agree to the <button 
                  type="button"
                  className="link" 
                  onClick={() => setShowTermsModal(true)}
                >
                  Terms of Service
                </button> and <button 
                  type="button"
                  className="link" 
                  onClick={() => setShowPrivacyModal(true)}
                >
                  Privacy Policy
                </button>
              </label>
              {errors.agreeToTerms && <span className="error-message">{errors.agreeToTerms}</span>}
            </div>

            <button 
              type="submit" 
              className="btn btn-primary btn-full"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>


          <div className="register-page__login">
            <p>Already have an account? <Link to="/login" className="link">Sign in here</Link></p>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-modal__icon">
              <span>🎉</span>
            </div>
            <h2 className="success-modal__title">Welcome to LIFE PSENsOR!</h2>
            <p className="success-modal__message">
              Your account has been created successfully. You can now start your wellness journey with us.
            </p>
            <div className="success-modal__actions">
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/panel');
                }}
              >
                Continue to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </div>
  );
};

export default RegisterPage;



